import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

const CATS = [["sc", "Società di capitali"], ["sp", "Società di persone"], ["pf", "Persone fisiche / Ditte"], ["730", "Modello 730"]];
const fmtIt = (d) => (d || "").split("-").reverse().join("/");
const fmtNum = (n) => Number(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseNum = (s) => { const n = parseFloat(String(s).trim().replace(/\./g, "").replace(",", ".")); return Number.isNaN(n) ? null : n; };
const looksNum = (s) => /^-?[\d.]{1,15}(,\d+)?$/.test(String(s).trim()) && /\d/.test(String(s));
const dispVal = (v) => {
  if (typeof v === "number") return fmtNum(v);
  if (v != null && v !== "" && looksNum(v)) { const n = parseNum(v); if (n !== null) return fmtNum(n); }
  return v ?? "";
};
const PF_SUBS = ["Ditte individuali", "Persone fisiche"];
const COL_CAT_LABELS = { sc: "Società di capitali", sp: "Società di persone", pf_piva: "Ditte individuali (con P.IVA)", pf_nopiva: "Persone fisiche (senza P.IVA)", 730: "Modello 730" };
const isSent = (r) => (r.category === "sc" || r.category === "sp") ? (r.sent_redditi && r.sent_irap) : r.sent;

export default function RedditiChecklist({ year: initialYear, date, onClose }) {
  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("sc");
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [newLink, setNewLink] = useState(null);
  const [sugg, setSugg] = useState([]);
  const suggT = useRef(null);
  const [editTargets, setEditTargets] = useState(false);
  const [tDates, setTDates] = useState([]);
  const [tBusy, setTBusy] = useState(false);
  const [newSub, setNewSub] = useState(PF_SUBS[0]);
  const [linkRowId, setLinkRowId] = useState(null);
  const [linkUp, setLinkUp] = useState(false);
  const [linkQ, setLinkQ] = useState("");
  const [linkSugg, setLinkSugg] = useState([]);
  const linkT = useRef(null);
  const [editCols, setEditCols] = useState(false);
  const [colCat, setColCat] = useState(null);
  const [colList, setColList] = useState([]);
  const [newColLabel, setNewColLabel] = useState("");

  useEffect(() => {
    clearTimeout(linkT.current);
    if (!linkRowId || linkQ.trim().length < 2) { setLinkSugg([]); return; }
    linkT.current = setTimeout(() => {
      api.get(`/scadenze/redditi/suggest?q=${encodeURIComponent(linkQ.trim())}`)
        .then((r) => setLinkSugg(r.data || [])).catch(() => setLinkSugg([]));
    }, 250);
    return () => clearTimeout(linkT.current);
  }, [linkQ, linkRowId]);

  useEffect(() => {
    clearTimeout(suggT.current);
    if (newLink || newName.trim().length < 2) { setSugg([]); return; }
    suggT.current = setTimeout(() => {
      api.get(`/scadenze/redditi/suggest?q=${encodeURIComponent(newName.trim())}`)
        .then((r) => setSugg(r.data || [])).catch(() => setSugg([]));
    }, 250);
    return () => clearTimeout(suggT.current);
  }, [newName, newLink]);

  const load = () => {
    setData(null);
    const d = year === initialYear && date ? `&date=${date}` : "";
    api.get(`/scadenze/redditi?year=${year}${d}`).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, [year]); // eslint-disable-line

  const toggle = async (row, field, value) => {
    if (!field.startsWith("values.")) {
      setData((d) => ({
        ...d,
        categories: {
          ...d.categories,
          [row.category]: d.categories[row.category].map((r) => r.id === row.id
            ? { ...r, [field]: value, ...(field === "applicable" && !value ? { done: false, checked: false, sent: false, sent_redditi: false, sent_irap: false } : {}) }
            : r),
        },
      }));
    }
    try { await api.put(`/scadenze/redditi/${row.id}`, { field, value }); }
    catch (e) {
      alert("⚠️ SALVATAGGIO NON RIUSCITO: " + (e.response?.data?.detail || e.message) + "\nControlla la connessione e riprova.");
      load();
    }
  };

  const addRow = async () => {
    if (!newName.trim()) return;
    try {
      const link = newLink || {};
      const r = await api.post("/scadenze/redditi", {
        year, category: cat, name: newName.trim(),
        sub: cat === "pf" ? newSub : null,
        company_id: link.kind === "company" ? link.id : null,
        person_id: link.kind === "person" ? link.id : null,
      });
      setData((d) => ({ ...d, categories: { ...d.categories, [cat]: [...d.categories[cat], r.data] } }));
      setNewName(""); setNewLink(null); setSugg([]);
      load();
    } catch (e) { alert("⚠️ Aggiunta non riuscita: " + (e.response?.data?.detail || e.message)); }
  };
  const delRow = async (row) => {
    if (!window.confirm(`Eliminare "${row.name}" dall'elenco ${year}?`)) return;
    setData((d) => ({ ...d, categories: { ...d.categories, [row.category]: d.categories[row.category].filter((r) => r.id !== row.id) } }));
    try { await api.delete(`/scadenze/redditi/${row.id}`); }
    catch (e) {
      alert("⚠️ Eliminazione non riuscita: " + (e.response?.data?.detail || e.message));
      load();
    }
  };
  const copyFromPrev = async () => {
    try {
      const r = await api.post(`/scadenze/redditi/init?year=${year}&from_year=${year - 1}`);
      if (!r.data.copied) alert(`Nel ${year - 1} non ci sono nominativi da riportare.\nSe i dati sono in un altro ambiente (es. preview), usa "⬆ Importa dati" con il file esportato da lì.`);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const exportData = async () => {
    try {
      const r = await api.get("/scadenze/redditi/export");
      const blob = new Blob([JSON.stringify(r.data)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `redditi-dati-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const importData = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const txt = await f.text();
      const r = await api.post("/scadenze/redditi/import", JSON.parse(txt));
      alert(`✅ Importati ${r.data.rows_imported} nominativi (+${r.data.targets_imported} impostazioni tappe).`);
      load();
    } catch (err) { alert("Import fallito: " + (err.response?.data?.detail || err.message)); }
  };
  const importExcel = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/scadenze/redditi/import-excel?year=${year}`, fd);
      const d = r.data;
      const det = Object.entries(d.per_sheet || {}).map(([s, v]) => `• ${s}: ${v.nuovi} nuovi, ${v.aggiornati} aggiornati`).join("\n");
      alert(`✅ Excel UNICO importato per il ${year}.\nNuovi nominativi: ${d.created} — Aggiornati: ${d.updated}\n\n${det}`);
      load();
    } catch (err) { alert("Import Excel fallito: " + (err.response?.data?.detail || err.message)); }
  };
  const linkExisting = async () => {
    try {
      const r = await api.post(`/scadenze/redditi/link-existing?year=${year}`);
      const d = r.data;
      let msg = `🔗 Collegati all'anagrafica: ${d.linked}`;
      if (d.ambiguous) msg += `\n⚠️ Ambigui (più corrispondenze, saltati): ${d.ambiguous}`;
      if (d.unmatched_count) msg += `\n❓ Non trovati in anagrafica (${d.unmatched_count}): ${d.unmatched.slice(0, 15).join(", ")}${d.unmatched_count > 15 ? "…" : ""}`;
      alert(msg);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const openTargets = () => {
    setTDates(data?.totals?.target_dates || []);
    setEditTargets(true);
  };
  const saveTargets = async () => {
    setTBusy(true);
    try {
      await api.put("/scadenze/redditi/targets", { year, dates: tDates.filter(Boolean) });
      setEditTargets(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setTBusy(false); }
  };
  const updateLocalRow = (row, patch) => {
    setData((d) => ({
      ...d,
      categories: { ...d.categories, [row.category]: d.categories[row.category].map((r) => r.id === row.id ? { ...r, ...patch } : r) },
    }));
  };
  const setLink = async (row, s) => {
    try {
      const field = s ? (s.kind === "company" ? "company_id" : "person_id") : "company_id";
      await api.put(`/scadenze/redditi/${row.id}`, { field, value: s ? s.id : null });
      updateLocalRow(row, s
        ? { company_id: s.kind === "company" ? s.id : null, person_id: s.kind === "person" ? s.id : null }
        : { company_id: null, person_id: null });
      setLinkRowId(null); setLinkQ(""); setLinkSugg([]);
    } catch (e) { alert("⚠️ Collegamento non riuscito: " + (e.response?.data?.detail || e.message)); }
  };
  const changeSub = async (row, v) => {
    try {
      await api.put(`/scadenze/redditi/${row.id}`, { field: "sub", value: v });
      load();
    } catch (e) { alert("⚠️ Spostamento non riuscito: " + (e.response?.data?.detail || e.message)); }
  };
  const openCols = (key) => { setColCat(key); setColList((data?.columns?.[key] || []).map((c) => ({ ...c }))); setNewColLabel(""); setEditCols(true); };
  const addCol = () => {
    const label = newColLabel.trim();
    if (!label) return;
    let key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30) || "col";
    while (colList.some((c) => c.key === key)) key += "2";
    setColList((a) => [...a, { key, label }]);
    setNewColLabel("");
  };
  const saveCols = async () => {
    try {
      await api.put("/scadenze/redditi/columns", { category: colCat, columns: colList });
      setEditCols(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };


  const stats = useMemo(() => {
    if (!data) return null;
    const all = Object.values(data.categories).flat().filter((r) => r.applicable);
    const done = all.filter((r) => r.done).length;
    const checked = all.filter((r) => r.checked).length;
    const sent = all.filter(isSent).length;
    const n = all.length;
    const wd = data.totals?.working_days_left || 0;
    const remaining = n - done;
    return { n, done, checked, sent,
      pctDone: n ? Math.round((done * 100) / n) : 0,
      pctChecked: n ? Math.round((checked * 100) / n) : 0,
      pctSent: n ? Math.round((sent * 100) / n) : 0,
      wd, remaining, perDay: wd > 0 && remaining > 0 ? Math.ceil(remaining / wd) : 0,
      target: data.totals?.target_date || null, targetLabel: data.totals?.target_label || "",
      expired: wd === 0 && (data.date || "") < new Date().toISOString().slice(0, 10) };
  }, [data]);

  const rows = (data?.categories?.[cat] || []).filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase()));
  const blocks = cat === "pf"
    ? [
      { title: "Ditte individuali (con P.IVA)", colKey: "pf_piva", rows: rows.filter((r) => r.sub === PF_SUBS[0]), cols: data?.columns?.pf_piva || [] },
      { title: "Persone fisiche (senza P.IVA)", colKey: "pf_nopiva", rows: rows.filter((r) => r.sub !== PF_SUBS[0]), cols: data?.columns?.pf_nopiva || [] },
    ]
    : [{ title: null, colKey: cat, rows, cols: data?.columns?.[cat] || [] }];
  const doubleSend = cat === "sc" || cat === "sp";
  const empty = data && Object.values(data.categories).every((l) => l.length === 0);

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="redditi-checklist-modal">
      <div className="modal" style={{ maxWidth: 1500, width: "97vw" }} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="redditi-close">✕</button>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginRight: 30 }}>
          <h3 style={{ margin: 0 }}>📑 Dichiarazioni dei Redditi</h3>
          <button className="btn sm" onClick={() => setYear((y) => y - 1)} data-testid="redditi-prev-year">◀</button>
          <b style={{ fontSize: 17 }} data-testid="redditi-year">{year}</b>
          <button className="btn sm" onClick={() => setYear((y) => y + 1)} data-testid="redditi-next-year">▶</button>
          <span className="muted" style={{ fontSize: 12.5 }}>Scadenza: <b>{fmtIt(data?.date || `${year}-10-31`)}</b></span>
          <div className="spacer" />
          <label className="btn sm" style={{ cursor: "pointer" }} title="Importa nominativi e valori dal file Excel UNICO (fogli SC, SP, PF_PIVA, PF, 730)" data-testid="redditi-import-excel-label">📊 Importa Excel UNICO
            <input type="file" accept=".xlsx" style={{ display: "none" }} onChange={importExcel} data-testid="redditi-import-excel-input" />
          </label>
          <button className="btn sm" onClick={exportData} title="Scarica tutti i dati in un file (per portarli in produzione)" data-testid="redditi-export-btn">⬇ Esporta dati</button>
          <label className="btn sm" style={{ cursor: "pointer" }} title="Carica un file esportato dall'altro ambiente" data-testid="redditi-import-label">⬆ Importa dati
            <input type="file" accept=".json,application/json" style={{ display: "none" }} onChange={importData} data-testid="redditi-import-input" />
          </label>
        </div>

        {!data && <p className="muted">Caricamento…</p>}
        {data && stats && (
          <>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center", margin: "10px 0" }}>
              <div style={{ minWidth: 150, flex: 1 }}>
                <div style={{ fontSize: 12.5, marginBottom: 3 }}>✅ Fatte: <b data-testid="rd-done">{stats.done}/{stats.n}</b> ({stats.pctDone}%)</div>
                <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${stats.pctDone}%`, background: "#39ff14" }} /></div>
              </div>
              <div style={{ minWidth: 150, flex: 1 }}>
                <div style={{ fontSize: 12.5, marginBottom: 3 }}>🔎 Controllate: <b data-testid="rd-checked">{stats.checked}/{stats.n}</b> ({stats.pctChecked}%)</div>
                <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${stats.pctChecked}%`, background: "#facc15" }} /></div>
              </div>
              <div style={{ minWidth: 150, flex: 1 }}>
                <div style={{ fontSize: 12.5, marginBottom: 3 }}>📤 Inviate: <b data-testid="rd-sent">{stats.sent}/{stats.n}</b> ({stats.pctSent}%)</div>
                <div className="fc-bar"><div className="fc-bar-fill" style={{ width: `${stats.pctSent}%`, background: "#38bdf8" }} /></div>
              </div>
              <div style={{ fontSize: 12.5 }} data-testid="rd-rate">
                {stats.expired
                  ? <span style={{ color: "#fb7185" }}>⏰ Scadenza passata</span>
                  : <>🎯 obiettivo: <b>{stats.targetLabel}</b>{" "}
                    <button className="btn sm" style={{ padding: "0 6px", fontSize: 11 }} title="Modifica le tappe obiettivo" onClick={openTargets} data-testid="redditi-targets-edit">✎</button><br />
                    📅 <b>{stats.wd}</b> gg lavorativi rimasti<br />
                    {stats.remaining > 0 ? <>⚡ <b>~{stats.perDay} al giorno</b> ({stats.remaining} da fare)</> : <span style={{ color: "#39ff14" }}>🎉 tutte fatte!</span>}</>}
              </div>
            </div>

            {editTargets && (
              <div className="panel" style={{ padding: 12, margin: "0 0 10px" }} data-testid="redditi-targets-editor">
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  🎯 <b>Tappe obiettivo {year}</b> — il ritmo giornaliero punta alla prossima tappa non ancora passata.
                  L'<b>invio finale</b> ({fmtIt(data.date)}) è sempre l'ultima tappa e non si tocca.
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {tDates.map((d, i) => (
                    <span key={i} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                      <input type="date" value={d} onChange={(e) => setTDates((a) => a.map((x, j) => j === i ? e.target.value : x))} data-testid={`redditi-target-date-${i}`} />
                      <button className="btn sm" style={{ padding: "0 6px" }} title="Rimuovi tappa" onClick={() => setTDates((a) => a.filter((_, j) => j !== i))} data-testid={`redditi-target-del-${i}`}>🗑</button>
                    </span>
                  ))}
                  <button className="btn sm" onClick={() => setTDates((a) => [...a, ""])} data-testid="redditi-target-add">＋ Aggiungi tappa</button>
                  <div className="spacer" />
                  <button className="btn sm" onClick={() => setEditTargets(false)}>Annulla</button>
                  <button className="btn sm primary" onClick={saveTargets} disabled={tBusy} data-testid="redditi-targets-save">{tBusy ? "…" : "💾 Salva tappe"}</button>
                </div>
              </div>
            )}
            {empty ? (
              <div style={{ textAlign: "center", padding: 20 }}>
                <p className="muted">Nessun nominativo per il {year}.</p>
                <button className="btn primary" onClick={copyFromPrev} data-testid="redditi-copy-prev">↩ Riporta i nominativi del {year - 1}</button>
                <p className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>I dati vivono nel database di QUESTO ambiente: se li vedi altrove (es. in preview), lì premi "⬇ Esporta dati" e qui "⬆ Importa dati" (pulsanti in alto).</p>
              </div>
            ) : (
              <>
                <div className="toolbar" style={{ marginTop: 0 }}>
                  {CATS.map(([c, lbl]) => (
                    <button key={c} className={"btn sm" + (cat === c ? " primary" : "")} onClick={() => setCat(c)} data-testid={`redditi-tab-${c}`}>
                      {lbl} ({(data.categories[c] || []).filter((r) => r.applicable).length})
                    </button>
                  ))}
                  <div className="spacer" />
                  {cat === "pf" ? (
                    <>
                      <button className="btn sm" onClick={() => openCols("pf_piva")} title="Colonne del blocco Ditte individuali (con P.IVA)" data-testid="redditi-cols-btn-piva">⚙ Colonne P.IVA</button>
                      <button className="btn sm" onClick={() => openCols("pf_nopiva")} title="Colonne del blocco Persone fisiche (senza P.IVA)" data-testid="redditi-cols-btn-nopiva">⚙ Colonne no P.IVA</button>
                    </>
                  ) : (
                    <button className="btn sm" onClick={() => openCols(cat)} title="Rinomina, aggiungi o elimina colonne di questo foglio" data-testid="redditi-cols-btn">⚙ Colonne</button>
                  )}
                  <button className="btn sm" onClick={linkExisting} title="Collega i nominativi all'anagrafica (match sul nome)" data-testid="redditi-link-existing">🔗 Collega anagrafica</button>
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Cerca…" style={{ width: 170 }} data-testid="redditi-search" />
                </div>
                {editCols && (
                  <div className="panel" style={{ padding: 12, margin: "8px 0" }} data-testid="redditi-cols-editor">
                    <div style={{ fontSize: 13, marginBottom: 8 }}>⚙ <b>Colonne di "{COL_CAT_LABELS[colCat] || colCat}"</b> — rinomina le intestazioni, elimina col 🗑 o aggiungi nuove colonne. I dati delle colonne eliminate restano salvati e ricompaiono se ricrei la colonna.</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {colList.map((c, i) => (
                        <span key={c.key} style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>
                          <input value={c.label} onChange={(e) => setColList((a) => a.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} style={{ width: 130, fontSize: 12 }} data-testid={`redditi-col-label-${c.key}`} />
                          <button className="btn sm" style={{ padding: "0 5px" }} title="Elimina colonna" onClick={() => setColList((a) => a.filter((_, j) => j !== i))} data-testid={`redditi-col-del-${c.key}`}>🗑</button>
                        </span>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <input value={newColLabel} onChange={(e) => setNewColLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCol()} placeholder="Nome nuova colonna…" style={{ width: 220 }} data-testid="redditi-col-new" />
                      <button className="btn sm" onClick={addCol} disabled={!newColLabel.trim()} data-testid="redditi-col-add">＋ Aggiungi colonna</button>
                      <div className="spacer" />
                      <button className="btn sm" onClick={() => setEditCols(false)}>Annulla</button>
                      <button className="btn sm primary" onClick={saveCols} data-testid="redditi-cols-save">💾 Salva colonne</button>
                    </div>
                  </div>
                )}
                <div className="rd-scroll" style={{ maxHeight: "52vh", overflow: "auto", marginTop: 8 }}>
                  {blocks.map((b) => (
                    <div key={b.colKey} style={{ width: "fit-content" }}>
                      {b.title && (
                        <div style={{ position: "sticky", left: 0, width: "fit-content", fontSize: 12, fontWeight: 800, textTransform: "uppercase", opacity: .8, padding: "10px 6px 4px" }} data-testid={`rd-block-${b.colKey}`}>
                          {b.title} ({b.rows.filter((r) => r.applicable).length})
                        </div>
                      )}
                      <table className="rd-table" style={{ minWidth: 900 + b.cols.length * 96 }}>
                        <thead><tr>
                          <th className="rd-sticky">Nominativo</th>
                          <th style={{ width: 88, textAlign: "center" }}>Interessato</th>
                          <th style={{ width: 66, textAlign: "center" }}>Fatta</th>
                          <th style={{ width: 88, textAlign: "center" }}>Controllata</th>
                          {doubleSend ? (
                            <>
                              <th style={{ width: 96, textAlign: "center" }}>Invio Redditi</th>
                              <th style={{ width: 86, textAlign: "center" }}>Invio IRAP</th>
                            </>
                          ) : <th style={{ width: 76, textAlign: "center" }}>Inviata</th>}
                          {b.cols.map((c) => <th key={c.key} style={{ minWidth: 88, fontSize: 11 }}>{c.label}</th>)}
                          <th style={{ minWidth: 150 }}>Note</th>
                          <th style={{ width: 34 }}></th>
                        </tr></thead>
                        <tbody>
                          {b.rows.map((r) => (
                            <tr key={r.id} style={{ opacity: r.applicable ? 1 : 0.45 }} data-testid={`rd-row-${r.id}`}>
                              <td className="rd-sticky" style={{ textDecoration: r.applicable ? "none" : "line-through", fontWeight: 600, position: "sticky", ...(linkRowId === r.id ? { zIndex: 60, overflow: "visible" } : {}) }}>
                                {r.company_id || r.person_id ? (
                                  <>
                                    <Link to={r.company_id ? `/aziende/${r.company_id}` : `/persone/${r.person_id}`}
                                      title="Apri la scheda cliente" style={{ color: "inherit" }} data-testid={`rd-link-${r.id}`}>
                                      🔗 {r.name}
                                    </Link>
                                    <button className="btn sm" style={{ padding: "0 4px", fontSize: 10, marginLeft: 4, opacity: .6 }} title="Scollega dall'anagrafica" onClick={() => setLink(r, null)} data-testid={`rd-unlink-${r.id}`}>✕</button>
                                  </>
                                ) : (
                                  <>
                                    {r.name}
                                    <button className="btn sm" style={{ padding: "0 5px", fontSize: 10, marginLeft: 4 }} title="Collega manualmente all'anagrafica" onClick={(e) => {
                                      const cont = e.currentTarget.closest(".rd-scroll");
                                      if (cont) {
                                        const cr = cont.getBoundingClientRect();
                                        const br = e.currentTarget.getBoundingClientRect();
                                        setLinkUp(br.top > cr.top + cr.height / 2);
                                      }
                                      setLinkRowId(linkRowId === r.id ? null : r.id); setLinkQ(r.name.split("(")[0].trim());
                                    }} data-testid={`rd-linkbtn-${r.id}`}>🔗+</button>
                                    {linkRowId === r.id && (
                                      <div style={{ position: "absolute", zIndex: 30, left: 0, ...(linkUp ? { bottom: "100%" } : { top: "100%" }), background: "#14224a", border: "1px solid rgba(122,162,255,.45)", borderRadius: 8, padding: 6, minWidth: 260, boxShadow: "0 8px 20px rgba(0,0,0,.5)" }} data-testid={`rd-linkbox-${r.id}`}>
                                        <input autoFocus value={linkQ} onChange={(e) => setLinkQ(e.target.value)} placeholder="Cerca in anagrafica…" style={{ width: "100%", fontSize: 12 }} data-testid={`rd-linkq-${r.id}`} />
                                        {linkSugg.map((s) => (
                                          <button key={`${s.kind}-${s.id}`} className="btn sm" style={{ display: "block", width: "100%", textAlign: "left", marginTop: 3 }} onClick={() => setLink(r, s)} data-testid={`rd-linkopt-${s.id}`}>
                                            {s.kind === "company" ? "🏢" : "👤"} {s.name}
                                          </button>
                                        ))}
                                        {linkQ.trim().length >= 2 && linkSugg.length === 0 && <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Nessun risultato in anagrafica.</div>}
                                      </div>
                                    )}
                                  </>
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}><input type="checkbox" checked={r.applicable} onChange={(e) => toggle(r, "applicable", e.target.checked)} data-testid={`rd-appl-${r.id}`} /></td>
                              <td style={{ textAlign: "center" }}><input type="checkbox" checked={r.done} disabled={!r.applicable} onChange={(e) => toggle(r, "done", e.target.checked)} data-testid={`rd-done-${r.id}`} /></td>
                              <td style={{ textAlign: "center" }}><input type="checkbox" checked={r.checked} disabled={!r.applicable} onChange={(e) => toggle(r, "checked", e.target.checked)} data-testid={`rd-checked-${r.id}`} /></td>
                              {doubleSend ? (
                                <>
                                  <td style={{ textAlign: "center" }}><input type="checkbox" checked={r.sent_redditi} disabled={!r.applicable} onChange={(e) => toggle(r, "sent_redditi", e.target.checked)} data-testid={`rd-sentred-${r.id}`} /></td>
                                  <td style={{ textAlign: "center" }}><input type="checkbox" checked={r.sent_irap} disabled={!r.applicable} onChange={(e) => toggle(r, "sent_irap", e.target.checked)} data-testid={`rd-sentirap-${r.id}`} /></td>
                                </>
                              ) : (
                                <td style={{ textAlign: "center" }}><input type="checkbox" checked={r.sent} disabled={!r.applicable} onChange={(e) => toggle(r, "sent", e.target.checked)} data-testid={`rd-sent-${r.id}`} /></td>
                              )}
                              {b.cols.map((c) => (
                                <td key={c.key} style={{ padding: "2px 3px" }}>
                                  <input defaultValue={dispVal((r.values || {})[c.key])}
                                    onBlur={(e) => {
                                      const raw = e.target.value.trim();
                                      const old = (r.values || {})[c.key];
                                      let val = raw;
                                      if (raw && looksNum(raw)) {
                                        const n = parseNum(raw);
                                        if (n !== null) { val = n; e.target.value = fmtNum(n); }
                                      }
                                      if (e.target.value !== dispVal(old)) toggle(r, `values.${c.key}`, val);
                                    }}
                                    style={{ width: 84, fontSize: 11.5, padding: "3px 5px" }}
                                    data-testid={`rd-val-${c.key}-${r.id}`} />
                                </td>
                              ))}
                              <td><input defaultValue={r.note || ""} onBlur={(e) => e.target.value !== (r.note || "") && toggle(r, "note", e.target.value)} placeholder="…" style={{ width: "100%", fontSize: 12, padding: "3px 6px" }} data-testid={`rd-note-${r.id}`} /></td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {cat === "pf" && (
                                  <select value={r.sub || PF_SUBS[1]} onChange={(e) => changeSub(r, e.target.value)} title="Blocco: con o senza P.IVA" style={{ fontSize: 10.5, padding: "1px 2px", maxWidth: 76, marginRight: 3 }} data-testid={`rd-sub-${r.id}`}>
                                    <option value={PF_SUBS[0]}>P.IVA</option>
                                    <option value={PF_SUBS[1]}>no P.IVA</option>
                                  </select>
                                )}
                                <button className="btn sm" style={{ padding: "0 6px" }} title="Elimina riga" onClick={() => delRow(r)} data-testid={`rd-del-${r.id}`}>🗑</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <div style={{ flex: 1, position: "relative" }}>
                    {sugg.length > 0 && (
                      <div className="rd-suggest" data-testid="redditi-suggest-list">
                        {sugg.map((s) => (
                          <button key={`${s.kind}-${s.id}`} onClick={() => { setNewName(s.name); setNewLink(s); setSugg([]); }} data-testid={`redditi-suggest-${s.id}`}>
                            {s.kind === "company" ? "🏢" : "👤"} {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <input value={newName} onChange={(e) => { setNewName(e.target.value); setNewLink(null); }} onKeyDown={(e) => e.key === "Enter" && addRow()}
                      placeholder={`+ Aggiungi nominativo in "${CATS.find(([c]) => c === cat)[1]}"… (cerca in anagrafica)`} style={{ width: "100%" }} data-testid="redditi-new-name" />
                    {newLink && <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>🔗 collegato a {newLink.kind === "company" ? "azienda" : "persona"} in anagrafica</div>}
                  </div>
                  <button className="btn sm primary" onClick={addRow} disabled={!newName.trim()} data-testid="redditi-add-btn">Aggiungi</button>
                  {cat === "pf" && (
                    <select value={newSub} onChange={(e) => setNewSub(e.target.value)} title="In quale blocco inserirlo" style={{ maxWidth: 240 }} data-testid="redditi-new-sub">
                      <option value={PF_SUBS[0]}>Ditte individuali (con P.IVA)</option>
                      <option value={PF_SUBS[1]}>Persone fisiche (senza P.IVA)</option>
                    </select>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
